// Share Trade-in — the Collection trade-in-mode toolbar + confirm dialog.
//
// Rendered by VaultPage while trade-in mode is active. Shows the running
// points total (mono), the tier-point legend, an overshoot warning when
// the selection exceeds 10 points, and a confirm button that enables at
// >= 10 points. Confirming opens a registry-voice AlertDialog spelling
// out that the trade is FINAL (shares retired) and a random sealed
// dossier is minted from the live drops.
//
// Design language: admin-calm paper/forest. The confirm affordance is a
// SECONDARY button (one primary per screen belongs elsewhere); the
// in-dialog confirm is the decisive action.

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  buttonVariants,
  type CertificateShare,
} from "@greenhold/components";

import { useTradeIn } from "./useTradeIn";
import {
  TIER_POINTS_LEGEND,
  TRADE_IN_POINTS_REQUIRED,
  forfeitedPoints,
  isOvershoot,
  meetsThreshold,
  sumPoints,
} from "./tradeInPoints";

export interface TradeInPanelProps {
  /// The opened shares currently selected for trade-in.
  selectedShares: CertificateShare[];
  /// Exit trade-in mode (called on Cancel + after a successful trade).
  onExit: () => void;
}

export function TradeInPanel({ selectedShares, onExit }: TradeInPanelProps) {
  const queryClient = useQueryClient();
  const mutation = useTradeIn();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const points = sumPoints(selectedShares);
  const canConfirm = meetsThreshold(points);
  const overshoot = isOvershoot(points);
  const forfeited = forfeitedPoints(points);

  async function handleConfirm() {
    try {
      const data = await mutation.mutateAsync({
        share_ids: selectedShares.map((s) => s.share_id),
      });
      toast.success(
        `Traded in ${data.burned_share_ids.length} share${
          data.burned_share_ids.length === 1 ? "" : "s"
        } — a new sealed dossier is in your collection.`,
      );
      setConfirmOpen(false);
      void queryClient.invalidateQueries({ queryKey: ["vault-items"] });
      onExit();
    } catch (err) {
      const detail = err as { code?: string; message?: string; reason?: string };
      if (detail?.code === "TRADE_IN_NO_LIVE_DROPS") {
        toast.error("There's nothing to mint right now — check back when a drop is live.");
        setConfirmOpen(false);
        return;
      }
      if (detail?.code === "TRADE_IN_SHARE_NOT_ELIGIBLE") {
        const msg =
          detail.reason === "has_active_listing"
            ? "One of these certificates is listed for sale — delist it first."
            : detail.reason === "already_retired"
              ? "One of these certificates has already been retired. Refreshing."
              : "One of these certificates isn't in your collection. Refreshing.";
        toast.error(msg);
        setConfirmOpen(false);
        void queryClient.invalidateQueries({ queryKey: ["vault-items"] });
        return;
      }
      toast.error(detail?.message ?? "Trade-in failed. Try again.");
    }
  }

  return (
    <section
      aria-label="Trade in shares"
      data-testid="trade-in-panel"
      className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-brand-forest-700 bg-brand-forest-900/40 p-4"
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-3">
          <span
            data-testid="trade-in-total"
            className="font-mono text-lg font-semibold text-brand-paper-100"
          >
            {points} / {TRADE_IN_POINTS_REQUIRED} points
          </span>
          <span className="font-body text-xs opacity-70">
            {selectedShares.length} selected
          </span>
        </div>
        <ul className="flex flex-wrap gap-x-3 gap-y-1 font-body text-[11px] opacity-70">
          {TIER_POINTS_LEGEND.map((t) => (
            <li key={t.tier}>
              {t.label} {t.points}
            </li>
          ))}
        </ul>
        {overshoot && (
          <p
            data-testid="trade-in-overshoot"
            className="font-body text-xs text-tier-founders-fg"
          >
            {forfeited} point{forfeited === 1 ? "" : "s"} above{" "}
            {TRADE_IN_POINTS_REQUIRED} are not returned.
          </p>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          // The ghost variant inks dark forest text, which is invisible on
          // this dark panel until hover — force light text + a light hover
          // wash (same treatment as the reveal ceremony's close button).
          className="!text-brand-paper-100 hover:!bg-white/10"
          onClick={onExit}
          disabled={mutation.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="secondary"
          disabled={!canConfirm || mutation.isPending}
          onClick={() => setConfirmOpen(true)}
          data-testid="trade-in-confirm-open"
        >
          Trade in {selectedShares.length > 0 ? `${selectedShares.length} ` : ""}
          for a dossier
        </Button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Trade in these certificates?</AlertDialogTitle>
            <AlertDialogDescription data-testid="trade-in-dialog-description">
              Trading in is final. The {selectedShares.length} selected certificate
              {selectedShares.length === 1 ? " is" : "s are"} permanently
              retired from the registry — worth {points} points, of which{" "}
              {TRADE_IN_POINTS_REQUIRED} are spent
              {forfeited > 0 ? ` and ${forfeited} forfeited` : ""}. In return,
              one sealed dossier drawn at random from the live drops is minted
              to your collection at no cost. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mutation.isPending}>
              Keep my shares
            </AlertDialogCancel>
            <AlertDialogAction
              className={buttonVariants({ variant: "destructive" })}
              disabled={mutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                void handleConfirm();
              }}
              data-testid="trade-in-confirm-button"
            >
              {mutation.isPending ? "Retiring…" : "Retire & mint"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
